import { AnyAction } from 'redux';
import { EffectsCommandMap, Subscription, SubscriptionAPI } from 'dva';
import { DvaReducer } from './shared';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import { ConnectState } from '@/models/connect';
import { OnUpdate_Data_Action } from '@/models/resourceSider';
import { Sider_OnUpdate_Data_Action } from '@/models/collectionManager';
import { isRssRelatedResource } from '@/utils/rss';

export interface ResourceInfoPageModelState {
  pageState: 'loading' | 'loaded';
  resourceID: string;
  resourceInfo: {
    coverImages: string[];
    resourceName: string;
    resourceTitle: string;
    resourceTypeCode: string;
    resourceType: string[];
    intro: string;
    tags: string[];
  } | null;
  /** RSS 导入单品：锁定标题/封面/简介等编辑 */
  isRssRelated: boolean;

  title_IsEditing: boolean;
  title_Input: string;
  title_Error: string;

  introduction_IsEditing: boolean;
  introduction_EditorText: string;
  introduction_EditorText_Error: string;

  hasPermission: boolean;
}

export interface ChangeAction extends AnyAction {
  type: 'change' | 'resourceInfoPage/change';
  payload: Partial<ResourceInfoPageModelState>;
}

export interface OnMount_Page_Action extends AnyAction {
  type: 'resourceInfoPage/onMount_Page';
  payload: {
    resourceID: string;
  };
}

export interface OnUnmount_Page_Action extends AnyAction {
  type: 'resourceInfoPage/onUnmount_Page';
}

export interface OnClick_EditTitleBtn_Action extends AnyAction {
  type: 'resourceInfoPage/onClick_EditTitleBtn';
}

export interface OnClick_CancelEditTitleBtn_Action extends AnyAction {
  type: 'resourceInfoPage/onClick_CancelEditTitleBtn';
}

export interface OnClick_SaveTitleBtn_Action extends AnyAction {
  type: 'resourceInfoPage/onClick_SaveEditTitleBtn';
  payload: {
    trigger: 'resource' | 'collection';
  };
}

export interface OnChange_TitleInput_Action extends AnyAction {
  type: 'resourceInfoPage/onChange_TitleInput';
  payload: {
    value: string;
  };
}

export interface OnClick_SaveIntroductionBtn_Action extends AnyAction {
  type: 'resourceInfoPage/onClick_SaveIntroductionBtn';
}

export interface OnChange_Cover_Action extends AnyAction {
  type: 'resourceInfoPage/onChange_Cover';
  payload: {
    value: string;
    trigger: 'resource' | 'collection';
  };
}

export interface OnChange_Labels_Action extends AnyAction {
  type: 'resourceInfoPage/onChange_Labels';
  payload: {
    value: string[];
  };
}

export interface OnChangeEditorAction extends AnyAction {
  type: 'resourceInfoPage/onChangeEditor';
  payload: string;
}

export interface ResourceInfoPageModelType {
  namespace: 'resourceInfoPage';
  state: ResourceInfoPageModelState;
  effects: {
    onMount_Page: (
      action: OnMount_Page_Action,
      effects: EffectsCommandMap,
    ) => void;
    onUnmount_Page: (
      action: OnUnmount_Page_Action,
      effects: EffectsCommandMap,
    ) => void;

    onClick_EditTitleBtn: (
      action: OnClick_EditTitleBtn_Action,
      effects: EffectsCommandMap,
    ) => void;
    onClick_CancelEditTitleBtn: (
      action: OnClick_CancelEditTitleBtn_Action,
      effects: EffectsCommandMap,
    ) => void;
    onClick_SaveEditTitleBtn: (
      action: OnClick_SaveTitleBtn_Action,
      effects: EffectsCommandMap,
    ) => void;
    onChange_TitleInput: (
      action: OnChange_TitleInput_Action,
      effects: EffectsCommandMap,
    ) => void;
    onClick_SaveIntroductionBtn: (
      action: OnClick_SaveIntroductionBtn_Action,
      effects: EffectsCommandMap,
    ) => void;
    onChange_Cover: (
      action: OnChange_Cover_Action,
      effects: EffectsCommandMap,
    ) => void;
    onChange_Labels: (
      action: OnChange_Labels_Action,
      effects: EffectsCommandMap,
    ) => void;
  };
  reducers: {
    change: DvaReducer<ResourceInfoPageModelState, ChangeAction>;
  };
  subscriptions: { setup: Subscription };
}

const initStates: ResourceInfoPageModelState = {
  pageState: 'loading',
  resourceID: '',
  resourceInfo: null,
  isRssRelated: false,

  title_IsEditing: false,
  title_Input: '',
  title_Error: '',

  introduction_IsEditing: false,
  introduction_EditorText: '',
  introduction_EditorText_Error: '',

  hasPermission: true,
};

const Model: ResourceInfoPageModelType = {
  namespace: 'resourceInfoPage',

  state: initStates,

  effects: {
    *onMount_Page(
      { payload }: OnMount_Page_Action,
      { put, call }: EffectsCommandMap,
    ) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          resourceID: payload.resourceID,
          pageState: 'loading',
        },
      });

      const params: Parameters<typeof FServiceAPI.Resource.info>[0] = {
        resourceIdOrName: payload.resourceID,
        isLoadPolicyInfo: 1,
        isTranslate: 1,
      };
      const {
        data: data_resourceInfo,
      }: {
        ret: number;
        errCode: number;
        msg: string;
        data: {
          coverImages: string[];
          resourceName: string;
          resourceTitle: string;
          resourceTypeCode: string;
          resourceType: string[];
          intro: string;
          tags: string[];
          feedUrl?: string;
          rssGuid?: string;
          rssPubDate?: string;
        };
      } = yield call(FServiceAPI.Resource.info, params);
      yield call(FUtil.Tool.promiseSleep, 1000);
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          resourceInfo: {
            coverImages: data_resourceInfo.coverImages,
            resourceName: data_resourceInfo.resourceName,
            resourceTypeCode: data_resourceInfo.resourceTypeCode,
            resourceType: data_resourceInfo.resourceType,
            resourceTitle: data_resourceInfo.resourceTitle,
            intro: data_resourceInfo.intro,
            tags: data_resourceInfo.tags,
          },
          isRssRelated: isRssRelatedResource(data_resourceInfo),
          pageState: 'loaded',
          introduction_EditorText: data_resourceInfo.intro,
        },
      });
    },
    *onUnmount_Page({}: OnUnmount_Page_Action, { put }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: initStates,
      });
    },

    *onClick_EditTitleBtn(
      {}: OnClick_EditTitleBtn_Action,
      { select, put }: EffectsCommandMap,
    ) {
      const { resourceInfoPage } = yield select(
        ({ resourceInfoPage }: ConnectState) => ({
          resourceInfoPage,
        }),
      );
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          title_IsEditing: true,
          title_Input: resourceInfoPage.resourceInfo.resourceTitle || '',
          title_Error: '',
        },
      });
    },
    *onClick_CancelEditTitleBtn(
      {}: OnClick_CancelEditTitleBtn_Action,
      { put }: EffectsCommandMap,
    ) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          title_IsEditing: false,
        },
      });
    },
    *onClick_SaveEditTitleBtn(
      { payload }: OnClick_SaveTitleBtn_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      const { resourceInfoPage } = yield select(
        ({ resourceInfoPage }: ConnectState) => ({
          resourceInfoPage,
        }),
      );

      const params: Parameters<typeof FServiceAPI.Resource.update>[0] = {
        resourceId: resourceInfoPage.resourceID,
        resourceTitle: resourceInfoPage.title_Input,
      };
      yield call(FServiceAPI.Resource.update, params);
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          resourceInfo: {
            ...resourceInfoPage.resourceInfo,
            resourceTitle: resourceInfoPage.title_Input,
          },
          title_IsEditing: false,
        },
      });
      if (payload.trigger === 'collection') {
        yield put<Sider_OnUpdate_Data_Action>({
          type: 'collectionManager/sider_onUpdate_Data',
        });
      } else {
        yield put<OnUpdate_Data_Action>({
          type: 'resourceSider/onUpdate_Data',
        });
      }
    },
    *onChange_TitleInput(
      { payload }: OnChange_TitleInput_Action,
      { put }: EffectsCommandMap,
    ) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          title_Input: payload.value,
          title_Error:
            payload.value === ''
              ? // ? '请输入标题'
                FI18n.i18nNext.t('naming_convention_resource_title_required')
              : payload.value.length > 100
              ? '不超过100个字符'
              : '',
        },
      });
    },
    *onClick_SaveIntroductionBtn(
      {}: OnClick_SaveIntroductionBtn_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      // console.log(' *********88888))))');
      const { resourceInfoPage } = yield select(
        ({ resourceInfoPage }: ConnectState) => ({
          resourceInfoPage,
        }),
      );

      const params: Parameters<typeof FServiceAPI.Resource.update>[0] = {
        resourceId: resourceInfoPage.resourceID,
        intro: resourceInfoPage.introduction_EditorText,
      };
      yield call(FServiceAPI.Resource.update, params);
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          resourceInfo: {
            ...resourceInfoPage.resourceInfo,
            intro: resourceInfoPage.introduction_EditorText.trim(),
          },
          introduction_IsEditing: false,
        },
      });
    },
    *onChange_Cover(
      { payload }: OnChange_Cover_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      const { resourceInfoPage } = yield select(
        ({ resourceInfoPage }: ConnectState) => ({
          resourceInfoPage,
        }),
      );

      const params: Parameters<typeof FServiceAPI.Resource.update>[0] = {
        resourceId: resourceInfoPage.resourceID,
        coverImages: [payload.value],
      };
      yield call(FServiceAPI.Resource.update, params);
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          resourceInfo: {
            ...resourceInfoPage.resourceInfo,
            coverImages: [payload.value],
          },
        },
      });
      if (payload.trigger === 'collection') {
        yield put<Sider_OnUpdate_Data_Action>({
          type: 'collectionManager/sider_onUpdate_Data',
        });
      } else {
        yield put<OnUpdate_Data_Action>({
          type: 'resourceSider/onUpdate_Data',
        });
      }
    },
    *onChange_Labels(
      { payload }: OnChange_Labels_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      const { resourceInfoPage } = yield select(
        ({ resourceInfoPage }: ConnectState) => ({
          resourceInfoPage,
        }),
      );

      const params: Parameters<typeof FServiceAPI.Resource.update>[0] = {
        resourceId: resourceInfoPage.resourceID,
        tags: payload.value,
      };
      yield call(FServiceAPI.Resource.update, params);
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          resourceInfo: {
            ...resourceInfoPage.resourceInfo,
            tags: payload.value,
          },
        },
      });
    },
  },

  reducers: {
    change(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
  },

  subscriptions: {
    setup({ dispatch, history }: SubscriptionAPI) {},
  },
};

export default Model;
